import { create } from 'zustand';

import { apiClient } from '../api/client.js';

export interface AuthUser {
  id: string;
  workspaceId: string;
  workspaceSlug: string;
  workspaceName: string;
  name: string;
  email: string;
  role: string;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, workspaceSlug?: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * `persist: false` — для встроенного режима. Токены встроенной сессии
   * живут только в памяти вкладки; если при этом записать пользователя
   * в localStorage, следующий обычный вход подхватит чужую личность
   * (сотрудника из кабинета) в паре со своим старым токеном.
   */
  setUser: (user: AuthUser | null, opts?: { persist?: boolean }) => void;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  workspace?: { id: string; name: string; slug: string };
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    workspace_id?: string;
    workspace_slug?: string;
    workspace_name?: string;
  };
}

const ACCESS_TOKEN_KEY = 'inbx.access_token.v2';
const REFRESH_TOKEN_KEY = 'inbx.refresh_token.v2';
const USER_KEY = 'inbx.user.v2';
// очищаем старый формат если есть
if (typeof localStorage !== 'undefined') {
  localStorage.removeItem('inbx.access_token');
  localStorage.removeItem('inbx.user');
}

function restore(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const user = JSON.parse(raw) as AuthUser;
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) apiClient.setAccessToken(token);
    return user;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: restore(),
  loading: false,
  error: null,
  async login(email, password, workspaceSlug) {
    set({ loading: true, error: null });
    try {
      const result = await apiClient.post<LoginResponse>('/v1/auth/login', {
        email,
        password,
        workspace_slug: workspaceSlug,
      });
      apiClient.setAccessToken(result.access_token);
      localStorage.setItem(ACCESS_TOKEN_KEY, result.access_token);
      if (result.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, result.refresh_token);
      const ws = result.workspace;
      const user: AuthUser = {
        id: result.user.id,
        workspaceId: ws?.id ?? result.user.workspace_id ?? '',
        workspaceSlug: ws?.slug ?? result.user.workspace_slug ?? workspaceSlug ?? '',
        workspaceName: ws?.name ?? result.user.workspace_name ?? workspaceSlug ?? '',
        name: result.user.name,
        email: result.user.email,
        role: result.user.role,
      };
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      set({ user, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'login failed' });
      throw err;
    }
  },
  async logout() {
    try {
      await apiClient.post('/v1/auth/logout');
    } finally {
      apiClient.setAccessToken(null);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      set({ user: null });
      // Tanstack Router beforeLoad срабатывает на навигации, не на изменении store —
      // принудительно ведём пользователя к /login, иначе он останется на текущей странице.
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
  },
  setUser(user, opts) {
    set({ user });
    if (opts?.persist === false) return;
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  },
}));
